import {
	type OrderShippingMethod,
	type PreviewOrderQuoteOfferFeasibilityInput,
	type PreviewOrderQuoteOfferFeasibilityQuery,
	usePreviewOrderQuoteOfferFeasibilityQuery,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';

type PreviewResult = PreviewOrderQuoteOfferFeasibilityQuery['previewOrderQuoteOfferFeasibility'];
/** Production-only feasibility floor (shipping excluded). */
/** One shipping option's feasibility verdict + breakdown. */
export type FeasibilityDelivery = PreviewResult['deliveries'][number];

/**
 * Debounced (~300ms) feasibility preview. Pass a fully-built input (or `null` to
 * stay idle) and get back the production-only floor plus a per-shipping-method
 * map of delivery verdicts. The `previewOrderQuoteOfferFeasibility` query is a
 * root query (not admin-scoped), so the client/org app can call it directly.
 */
export function useFeasibilityPreview(input: PreviewOrderQuoteOfferFeasibilityInput | null) {
	const inputKey = input ? JSON.stringify(input) : '';
	const [debounced, setDebounced] = useState<PreviewOrderQuoteOfferFeasibilityInput | null>(null);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(input), 300);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on the serialized input only
	}, [inputKey]);

	const { data, loading } = usePreviewOrderQuoteOfferFeasibilityQuery({
		variables: { input: debounced ?? {} },
		skip: !debounced,
		fetchPolicy: 'no-cache',
	});

	const result = data?.previewOrderQuoteOfferFeasibility;
	const production = result?.production ?? null;
	const byMethod = new Map<OrderShippingMethod, FeasibilityDelivery>(
		(result?.deliveries ?? []).map((d) => [d.shippingMethod, d])
	);

	return { production, byMethod, loading };
}
